/**
 * Corporate bulk import (item 8) — an Excel workbook or a CSV.
 *
 * Staff pick a corporate partner, choose the file that partner sent, and upload
 * it. The panel does no parsing of its own — it reads the file to text and posts
 * it, and the server returns a per-row report, because partial success is the
 * normal case: some rows land as volunteers, some are skipped as duplicates, and
 * some are rejected, all in one pass.
 *
 * Every imported volunteer is created PENDING and linked to the chosen partner —
 * a bulk-imported person has done no mandatory training yet, and the activation
 * gate (item 7) would refuse to make them active anyway.
 */

import { useState } from 'react';
import type { VolunteerImportResult } from '@parinaam-vms/shared';
import { Button, Card, SelectInput } from '../../components/common';
import type { SelectOption } from '../../components/common';
import { useOrganizations, useImportVolunteers } from '../../hooks/useOrganizations';
import { downloadImportTemplate } from '../../services/api/organizations';
import type { ImportPayload } from '../../services/api/organizations';
import { ApiRequestError } from '../../services/api/client';
import styles from './BulkImportPanel.module.css';

export interface BulkImportPanelProps {
  onClose: () => void;
}

export function BulkImportPanel({ onClose }: BulkImportPanelProps) {
  const [organizationId, setOrganizationId] = useState('');
  const [payload, setPayload] = useState<ImportPayload | null>(null);
  const [fileName, setFileName] = useState('');
  const [templating, setTemplating] = useState(false);
  const [formError, setFormError] = useState<string | undefined>();

  const organizations = useOrganizations();
  const importer = useImportVolunteers(organizationId);

  const organizationOptions: SelectOption[] = (organizations.data ?? []).map((organization) => ({
    value: organization.organization_id,
    label: organization.city ? `${organization.name} — ${organization.city}` : organization.name,
  }));

  /**
   * Read the file the way its format needs, and say which was which.
   *
   * The extension decides, not the content. A workbook read as text is
   * mojibake and a CSV read as base64 is a wasted round trip — and the browser
   * already knows which the person picked, so guessing from bytes would be
   * choosing to know less.
   */
  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setFormError(undefined);
    setPayload(null);

    const isWorkbook = /.xlsx$/i.test(file.name);
    const reader = new FileReader();

    reader.onerror = () =>
      setFormError('That file could not be read. Try choosing it again.');

    if (isWorkbook) {
      reader.onload = () => {
        // A data URL, so the base64 is already done — the alternative is
        // walking an ArrayBuffer by hand to avoid blowing the stack on
        // String.fromCharCode(...bytes).
        const url = String(reader.result ?? '');
        const base64 = url.slice(url.indexOf(',') + 1);
        setPayload(base64 ? { xlsx: base64 } : null);
      };
      reader.readAsDataURL(file);
    } else {
      reader.onload = () => {
        const text = String(reader.result ?? '');
        setPayload(text.trim() ? { csv: text } : null);
      };
      reader.readAsText(file);
    }
  };

  const onTemplate = async () => {
    setTemplating(true);
    setFormError(undefined);
    try {
      await downloadImportTemplate();
    } catch {
      setFormError('The template could not be downloaded. Try again in a moment.');
    } finally {
      setTemplating(false);
    }
  };

  const submit = () => {
    if (!organizationId) {
      setFormError('Choose the corporate partner these volunteers belong to.');
      return;
    }
    if (!payload) {
      setFormError('Choose an Excel or CSV file to import.');
      return;
    }
    setFormError(undefined);
    importer.mutate(payload);
  };

  const result = importer.data;
  const serverError =
    importer.isError && importer.error instanceof ApiRequestError ? importer.error.message : null;

  return (
    <Card title="Import corporate volunteers">
      {result ? (
        <ImportReport result={result} onDone={onClose} />
      ) : (
        <div className={styles.form}>
          <p className={styles.intro}>
            For a corporate partner who has shared their volunteers' details. An Excel workbook
            (<code>.xlsx</code>) or a CSV, with a header row of <code>first_name</code>,{' '}
            <code>last_name</code>, and <code>email</code> or <code>phone</code>. Everyone is added
            as pending and linked to the partner you choose.
          </p>

          {(formError || serverError) && (
            <p className={styles.formError} role="alert">
              {formError ?? serverError}
            </p>
          )}

          <SelectInput
            label="Corporate partner"
            required
            placeholder={organizations.isPending ? 'Loading…' : 'Choose the partner'}
            options={organizationOptions}
            value={organizationId}
            onChange={(event) => {
              setOrganizationId(event.target.value);
              setFormError(undefined);
            }}
          />

          <div className={styles.file}>
            <label className={styles.fileLabel} htmlFor="import-file">
              Excel or CSV file
            </label>
            <input
              id="import-file"
              type="file"
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFile}
              disabled={importer.isPending}
            />
            {fileName && <span className={styles.fileName}>{fileName}</span>}
          </div>

          {/*
            The template answers the only two questions this import ever
            refuses on — what the columns are called, and what a phone number
            should look like — so it sits beside the file picker rather than in
            a help page nobody opens.
          */}
          <p className={styles.templateHint}>
            Not sure of the columns?{' '}
            <button
              type="button"
              className={styles.templateLink}
              onClick={() => void onTemplate()}
              disabled={templating || importer.isPending}
            >
              {templating ? 'Preparing…' : 'Download the template'}
            </button>{' '}
            — fill it in, delete the example rows, and upload it back.
          </p>

          <div className={styles.actions}>
            <Button variant="primary" loading={importer.isPending} onClick={submit}>
              Import volunteers
            </Button>
            <Button onClick={onClose} disabled={importer.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

const OUTCOME_LABEL: Record<VolunteerImportResult['rows'][number]['outcome'], string> = {
  created: 'Added',
  skipped_duplicate: 'Skipped — duplicate',
  error: 'Not imported',
};

function ImportReport({ result, onDone }: { result: VolunteerImportResult; onDone: () => void }) {
  return (
    <div className={styles.report}>
      <p className={styles.summary}>
        <strong>{result.organization_name}</strong>: {result.created} added, {result.skipped}{' '}
        skipped, {result.errored} not imported, out of {result.total} row
        {result.total === 1 ? '' : 's'}.
      </p>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Row</th>
              <th>Name</th>
              <th>Outcome</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => (
              <tr key={row.line}>
                <td>{row.line}</td>
                <td>{row.name ?? '—'}</td>
                <td>
                  <span className={styles[row.outcome]}>{OUTCOME_LABEL[row.outcome]}</span>
                </td>
                <td>{row.volunteer_code ?? row.message ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.actions}>
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
